<script setup>
import { computed, ref } from 'vue'
import flagUkraine from '../assets/flags/ukraine.svg'
import flagRussia from '../assets/flags/russia.svg'
import flagUnitedKingdom from '../assets/flags/united-kingdom.svg'
import lockIcon from '../assets/icons/lock.svg'
import nrwLoginLogo from '../assets/brand/nrw-login-logo.png'
import stationReportingIllustration from '../assets/login/station-reporting-illustration.png'
import PwaInstallPrompt from '../components/PwaInstallPrompt.vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from '../i18n'
import { changeGuestLanguage, signIn } from '../session'

const router = useRouter()
const route = useRoute()
const { locale, t } = useI18n()
const username = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')
const languageMenu = ref(null)
const reportLanguages = [
  { code: 'uk', labelKey: 'ukrainian', flag: flagUkraine },
  { code: 'ru', labelKey: 'russian', flag: flagRussia },
  { code: 'en', labelKey: 'english', flag: flagUnitedKingdom },
]

const activeLanguage = computed(() => reportLanguages.find((language) => language.code === locale.value) ?? reportLanguages[0])
const sessionNotice = computed(() => route.query.reason === 'session-revoked' ? t('sessionRevoked') : '')

function setGuestLanguage(language) {
  changeGuestLanguage(language)
  languageMenu.value?.removeAttribute('open')
}

async function submit() {
  error.value = ''
  loading.value = true
  try {
    const user = await signIn(username.value, password.value)
    const target = user.mustChangePassword
      ? { name: 'change-password', query: route.query.redirect ? { redirect: route.query.redirect } : {} }
      : route.query.redirect || '/'
    await router.push(target)
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <section class="login-layout" :aria-label="t('loginTitle')">
    <aside class="login-brand-panel">
      <img class="login-illustration" :src="stationReportingIllustration" alt="" />
      <div class="login-brand-content">
        <img class="login-brand-logo" :src="nrwLoginLogo" alt="Next Reports Web" />
        <span class="login-brand-rule" aria-hidden="true"></span>
        <p class="login-brand-description">{{ t('loginBrandDescription') }}</p>
      </div>
    </aside>

    <main class="login-entry">
      <PwaInstallPrompt />
      <details ref="languageMenu" class="login-language-menu">
        <summary class="login-language-trigger" :aria-label="t('language')" :title="t(activeLanguage.labelKey)">
          <img :src="activeLanguage.flag" alt="" />
          <span>{{ t(activeLanguage.labelKey) }}</span>
        </summary>
        <div class="login-language-popover" :aria-label="t('language')" role="group">
          <button v-for="language in reportLanguages" :key="language.code" class="login-language-option" type="button"
            :class="{ active: locale === language.code }" :aria-label="t(language.labelKey)" @click="setGuestLanguage(language.code)">
            <img :src="language.flag" alt="" />
            <span>{{ t(language.labelKey) }}</span>
          </button>
        </div>
      </details>

      <div class="login-form-surface">
        <h1>{{ t('loginTitle') }}</h1>
        <p class="login-form-hint">{{ t('loginFormHint') }}</p>
        <p v-if="sessionNotice" class="notice" role="status">{{ sessionNotice }}</p>
        <form class="form-stack login-form" @submit.prevent="submit">
          <label>{{ t('username') }}
            <input v-model="username" autocomplete="username" maxlength="64" required />
          </label>
          <label>{{ t('password') }}
            <input v-model="password" type="password" autocomplete="current-password" required />
          </label>
          <p v-if="error" class="notice error" role="alert">{{ error }}</p>
          <button class="button login-submit" :disabled="loading" type="submit">{{ loading ? t('signingIn') : t('signIn') }}</button>
        </form>
        <p class="login-security">
          <span class="login-security-icon" aria-hidden="true"><img :src="lockIcon" alt="" /></span>
          <span>{{ t('loginSecurity') }}</span>
        </p>
      </div>
    </main>
  </section>
</template>
